<template>
  <section class="section">
    <!-- Модалка обработки файла -->
    <div v-if="isParsing" class="modal-overlay">
      <div class="modal-card">
        <LoadingLogo class="modal-spinner" />
        <div class="modal-text">Файл обрабатывается…</div>
      </div>
    </div>

    <LoadingLogo v-if="isDataLoading" class="section__loader" />
    <div class="section__wrapper" v-else>
      <div class="section__header header">
        <div class="header__left">
          <h1 class="section__heading">
            <Header>
              Статистика <span>— {{ settings.selectedYear }}</span>
            </Header>
          </h1>
        </div>

        <div class="header__right">
          <!-- Кнопка загрузки отчёта: только в общем режиме -->
          <button
            v-if="!releaseUuid"
            class="upload-btn"
            type="button"
            @click="triggerFileSelect"
            :disabled="isParsing"
            title="Загрузить CSV отчёт"
          >
            <svg viewBox="0 0 24 24" class="upload-icon" aria-hidden="true">
              <path
                d="M12 16v-8m0 0l-4 4m4-4l4 4M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2"
                stroke="currentColor"
                stroke-width="1.5"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            Загрузить отчёт
          </button>

          <!-- Скрытый input для выбора CSV -->
          <input
            v-if="!releaseUuid"
            ref="fileInput"
            type="file"
            class="hidden"
            accept=".csv,text/csv"
            @change="onFileChange"
          />

          <label class="year">
            <span>Year:</span>
            <select
              class="year__select"
              :value="String(settings.selectedYear)"
              @change="onYearChange($event.target.value)"
            >
              <option v-for="y in settings.yearOptions" :key="y" :value="y">
                {{ y }}
              </option>
            </select>
          </label>
        </div>
      </div>

      <div class="section__statistics">
        <!-- Линейный график по месяцам -->
        <div class="section__block" v-if="workingStatistics.length">
          <div class="section__linechart">
            <LineChart
              class="chart"
              :chart-data="lineConfig.data"
              :options="lineConfig.options"
            />
          </div>
        </div>

        <!-- Пончиковая диаграмма + список площадок -->
        <div class="section__block">
          <h2 class="section__heading block__heading" v-if="workingStatistics.length">
            Streams and stores ({{ settings.selectedYear }})
          </h2>

          <div class="section__stores stores">
            <div class="stores__piechart">
              <div class="stores__total">
                <p>
                  {{ formatNumber(totalStreams) }}<br />
                  <span>streams</span>
                </p>
              </div>

              <DoughnutChart
                class="chart"
                :chart-data="pieConfig.data"
                :options="pieConfig.options"
              />
            </div>

            <div class="section__list list list--stores">
              <div
                class="list__item"
                v-for="store in storesSortedByStreams"
                :key="store.key"
                :style="`--color: ${store.color}`"
              >
                <p>
                  {{ store.name }}
                  <span v-if="(storeTotalsMap[store.key] || 0) > 0">
                    — {{ storePercent(store.key) }}% ({{ formatNumber(storeTotalsMap[store.key] || 0) }})
                  </span>
                  <span v-else>— 0</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div> <!-- /statistics -->
    </div> <!-- /wrapper -->
  </section>
</template>

<script>
import Header from '@/components/Header.vue'
import LoadingLogo from "@/components/LoadingLogo.vue";
import { Chart, registerables } from "chart.js";
import { LineChart, DoughnutChart } from "vue-chart-3";
import axios from "@/axios";
import { toast } from "vue3-toastify";
// import "vue3-toastify/dist/index.css"; // если не подключено глобально

Chart.register(...registerables);

export default {
  name: "Statistics",
  components: { LoadingLogo, LineChart, DoughnutChart, Header },

  data() {
    const now = new Date();
    const currentYear = now.getFullYear();
    return {
      isDataLoading: true,
      isParsing: false,
      // локальные данные для режима конкретного релиза (уже pivoted в wide-формат)
      releaseStatsWide: [],
      // кеш цветов для динамических площадок
      colorCache: {},
      settings: {
        selectedYear: currentYear,
        yearOptions: [
          String(currentYear),
          String(currentYear - 1),
          String(currentYear - 2),
        ],
      },
    };
  },

  computed: {
    // uid релиза (если есть): маршрут должен содержать :release_uuid
    releaseUuid() {
      return this.$route?.params?.release_uuid || null;
    },

    // --- геттеры стора (для общего режима) ---
    commonStatisticsFromStore() {
      return (
        this.$store.getters["statistics/commonStatistics"] ??
        this.$store.getters["commonStatistics"] ??
        []
      );
    },
    storeKeysFromStore() {
      return (
        this.$store.getters["statistics/storeKeys"] ??
        this.$store.getters["storeKeys"] ??
        []
      );
    },
    storeNamesFromStore() {
      return (
        this.$store.getters["statistics/storeNames"] ??
        this.$store.getters["storeNames"] ??
        []
      );
    },
    storeColorsFromStore() {
      return (
        this.$store.getters["statistics/storeColors"] ??
        this.$store.getters["storeColors"] ??
        []
      );
    },

    // --- активный источник данных для графиков (общий vs релиз) ---
    workingStatistics() {
      // в релизном режиме используем локально пивотнутые данные,
      // иначе — общий стор
      return this.releaseUuid ? this.releaseStatsWide : this.commonStatisticsFromStore;
    },

    // ключи, реально встречающиеся в данных (кроме date)
    derivedKeys() {
      const set = new Set();
      for (const day of this.workingStatistics) {
        for (const key of Object.keys(day)) {
          if (key !== 'date' && Number(day[key] || 0) > 0) set.add(key);
        }
      }
      return Array.from(set);
    },

    // ready справочник площадок = из стора + динамические из данных
    storesRef() {
      const base = this.storeKeysFromStore.map((key, i) => ({
        key,
        name: this.storeNamesFromStore[i],
        color: this.storeColorsFromStore[i],
        __base: true,
      }));

      const baseKeys = new Set(base.map(s => s.key));
      const extra = this.derivedKeys
        .filter(k => !baseKeys.has(k))
        .map(k => ({
          key: k,
          name: this.humanizeKey(k),
          color: this.getColorForKey(k),
          __base: false,
        }));

      return [...base, ...extra];
    },

    // ===== ИСПРАВЛЕНО: общий total из тех же данных, что и пончик/список =====
    totalStreams() {
      return this.filteredDaysByYear.reduce((acc, day) => {
        const daySum = Object.entries(day)
          .filter(([k]) => k !== 'date')
          .reduce((s, [, v]) => s + Number(v || 0), 0);
        return acc + daySum;
      }, 0);
    },

    // Отфильтрованные дни по году
    filteredDaysByYear() {
      const y = String(this.settings.selectedYear);
      return this.workingStatistics.filter((d) =>
        String(d.date).startsWith(`${y}-`)
      );
    },

    // Суммы по площадкам за год
    platformTotals() {
      const buckets = {};
      for (const day of this.filteredDaysByYear) {
        for (const [key, val] of Object.entries(day)) {
          if (key === 'date') continue;
          const v = Number(val || 0);
          buckets[key] = (buckets[key] || 0) + v;
        }
      }
      return buckets;
    },

    storeTotalsMap() {
      const map = {};
      for (const s of this.storesRef) map[s.key] = this.platformTotals[s.key] || 0;
      return map;
    },

    storesSortedByStreams() {
      return [...this.storesRef].sort(
        (a, b) =>
          (this.storeTotalsMap[b.key] || 0) - (this.storeTotalsMap[a.key] || 0)
      );
    },

    // ===== Линейный график по месяцам =====
    lineConfig() {
      const y = this.settings.selectedYear;
      const monthsISO = [];
      for (let m = 0; m < 12; m++)
        monthsISO.push(`${y}-${("0" + (m + 1)).slice(-2)}`);
      const labels = this.monthLabels();

      // store -> month -> sum
      const perStoreMonthly = {};
      for (const day of this.filteredDaysByYear) {
        const d = new Date(day.date);
        if (Number.isNaN(d.getTime())) continue;
        const mk = `${d.getFullYear()}-${("0" + (d.getMonth() + 1)).slice(-2)}`;

        for (const [key, val] of Object.entries(day)) {
          if (key === 'date') continue;
          const qty = Number(val || 0);
          if (!perStoreMonthly[key]) perStoreMonthly[key] = {};
          perStoreMonthly[key][mk] = (perStoreMonthly[key][mk] || 0) + qty;
        }
      }

      const datasets = Object.keys(perStoreMonthly)
        .filter((key) => Object.values(perStoreMonthly[key]).some((v) => v > 0))
        .map((key) => {
          const { color, label } = this.resolveStoreStyle(key);
          const data = monthsISO.map((mk) => perStoreMonthly[key][mk] ?? 0);
          return {
            label,
            data,
            borderColor: color,
            backgroundColor: this.hexToRgba(color, 0.12),
            fill: true,
            tension: 0.5,
          };
        });

      return {
        type: "line",
        data: { labels, datasets },
        options: {
          responsive: true,
          interaction: { mode: "index", intersect: false },
          plugins: {
            legend: { display: true, position: "bottom" },
            tooltip: {
              enabled: true,
              backgroundColor: "white",
              titleColor: "#222",
              bodyColor: "#222",
              bodyFont: { weight: "500", size: 13 },
              callbacks: {
                label: (ctx) =>
                  `${ctx.dataset.label}: ${this.formatK(ctx.parsed.y ?? 0)}`,
              },
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                callback: (v) => this.formatK(v),
              },
            },
          },
        },
      };
    },

    // ===== Пончик по долям площадок =====
    pieConfig() {
      const entries = Object.entries(this.platformTotals)
        .filter(([, val]) => val > 0)
        .sort((a, b) => b[1] - a[1]);

      const labels = entries.map(([key]) => this.resolveStoreStyle(key).label);
      const data = entries.map(([, val]) => val);
      const colors = entries.map(([key]) => this.resolveStoreStyle(key).color);

      return {
        type: "doughnut",
        data: {
          labels,
          datasets: [{ data, backgroundColor: colors, hoverOffset: 4 }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          borderRadius: 4,
          borderWidth: 2,
          hoverBorderWidth: 0,
          plugins: { legend: { display: false } },
        },
      };
    },
  },

  methods: {
    // =================== Загрузка данных ===================
    async fetchData() {
      const y = this.settings.selectedYear;
      const start_date = this.getDashFormattedDate(new Date(y, 0, 1));
      const end_date   = this.getDashFormattedDate(new Date(y, 11, 31));

      this.isDataLoading = true;
      try {
        if (this.releaseUuid) {
          // Режим конкретного релиза: запрос узкого формата -> пивот в wide
          const body = {
            release_uuid: this.releaseUuid,
            start_date,
            end_date,
            grouped: false, // DTO по-прежнему может требовать это поле
          };
          const { data } = await axios.post('/track-statistics', body);
          this.releaseStatsWide = this.pivotNarrowToWide(data || []);
        } else {
          // Общий режим: берём из стора
          const hasNs = !!this.$store._modulesNamespaceMap["statistics/"];
          await this.$store.dispatch(
            hasNs ? "statistics/fetchCommonStreams" : "fetchCommonStreams",
            { start_date, end_date }
          );
        }
      } finally {
        this.isDataLoading = false;
      }
    },

    // Узкий -> широкий
    pivotNarrowToWide(rows) {
      // rows: [{ day, platform, totalQuantity }]
      const byDay = new Map();
      for (const r of rows) {
        if (!r) continue;
        const dayISO = this.ensureISODate(r.day);
        const platKey = this.normalizePlatform(r.platform);
        const qty = Number(r.totalQuantity || 0);

        if (!byDay.has(dayISO)) byDay.set(dayISO, { date: dayISO });
        const row = byDay.get(dayISO);
        row[platKey] = (row[platKey] || 0) + qty;
      }

      const out = Array.from(byDay.values()).sort((a, b) => a.date.localeCompare(b.date));
      return out;
    },

    ensureISODate(val) {
      if (!val) return '';
      if (val instanceof Date) return val.toISOString().slice(0,10);
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) return String(val).slice(0,10);
      return d.toISOString().slice(0,10);
    },

    // =================== UI: год ===================
    onYearChange(yearStr) {
      const y = parseInt(yearStr, 10);
      if (!Number.isNaN(y)) this.settings.selectedYear = y;
    },

    monthLabels() {
      const base = new Date(this.settings.selectedYear, 0, 1);
      const labels = [];
      for (let m = 0; m < 12; m++) {
        const d = new Date(base.getFullYear(), m, 1);
        labels.push(d.toLocaleString("en-US", { month: "short" }));
      }
      return labels;
    },

    // =================== CSV Загрузка (только общий режим) ===================
    triggerFileSelect() {
      const input = this.$refs.fileInput;
      if (input) input.click();
    },

    async onFileChange(e) {
      const file = e?.target?.files?.[0];
      if (e?.target) e.target.value = '';
      if (!file) return;

      const name = file.name?.toLowerCase() || '';
      if (!name.endsWith('.csv')) {
        toast.error("Ожидается файл .csv");
        return;
      }

      try {
        this.isParsing = true;
        const fd = new FormData();
        fd.append('file', file);
        await axios.post('/parse-file', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success("Отчёт загружен. Обработка запущена.");
        // если парсинг синхронный и данные уже есть — обновим
        // await this.fetchData();
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Не удалось обработать файл";
        toast.error(msg);
      } finally {
        this.isParsing = false;
      }
    },

    // =================== Вспомогательные ===================
    resolveStoreStyle(key) {
      const store = this.storesRef.find((s) => s.key === key);
      if (store) return { color: store.color, label: store.name };
      // fallback
      return { color: this.getColorForKey(key), label: this.humanizeKey(key) };
    },

    getColorForKey(key) {
      if (this.colorCache[key]) return this.colorCache[key];
      const colors = [
        "#FF1E8A", "#64D561", "#64BCED", "#F21818",
        "#EF9F1A", "#7C3AED", "#10B981", "#F59E0B",
        "#14B8A6", "#3B82F6", "#8B5CF6", "#EC4899"
      ];
      const c = colors[Math.floor(Math.random() * colors.length)];
      this.colorCache[key] = c;
      return c;
    },

    humanizeKey(slug) {
      return String(slug)
        .split('_')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
    },

    hexToRgba(hex, alpha = 0.15) {
      const m = hex.replace("#", "");
      const bigint = parseInt(m, 16);
      const r = (bigint >> 16) & 255;
      const g = (bigint >> 8) & 255;
      const b = bigint & 255;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },

    storePercent(key) {
      const total = this.totalStreams || 0;
      const val = this.storeTotalsMap[key] || 0;
      if (!total) return 0;
      return Math.round((val * 1000) / total) / 10; // один знак после запятой
    },

    formatK(v) {
      const n = Number(v || 0);
      return n >= 1000 ? (n / 1000).toFixed(1) + "k" : String(n);
    },
    formatNumber(v) {
      return Number(v || 0).toLocaleString("en-US");
    },

    getDashFormattedDate(date) {
      return (
        date.getFullYear() +
        "-" +
        ("0" + (date.getMonth() + 1)).slice(-2) +
        "-" +
        ("0" + date.getDate()).slice(-2)
      );
    },

    // ===== НОРМАЛИЗАЦИЯ ПЛАТФОРМ (в релизном режиме) =====
    normalizePlatform(platform) {
      if (!platform) return 'all';
      const p = String(platform).trim().toLowerCase();

      const youtubeSet = new Set([
        'youtube','youtube artist','youtube content id','youtube music','youtube premium',
        'youtube ugc','youtube official content','youtube audio tier','youtube shorts',
        'believe rights services (youtube)'
      ]);
      const vkSet = new Set(['vk','uma vk music','uma vk clips','uma (vkontakte)','uma (odnoklassniki)']);
      const spotifySet = new Set(['spotify','spotify free','spotify premium']);
      const appleMusicSet = new Set(['apple music','shazam']);
      const itunesSet = new Set(['itunes','itunes match']);
      const amazonSet = new Set(['amazon','amazon music','amazon music streaming']);
      const instagramSet = new Set(['instagram','instagram reels','instagram stories']);
      const facebookSet = new Set(['facebook','facebook ad breaks','facebook fan subscription','facebook reels','facebook right manager']);
      const tiktokSet = new Set(['tiktok','tiktok commerce','tiktok music','douyin']);
      const tencentSet = new Set(['tencent qq music','tencent kugou','tencent kuwo']);
      const yandexSet = new Set(['yandex music','yandex','yango']);

      const directMap = {
        'boom': 'boom',
        'deezer': 'deezer',
        'jiosaavn': 'saavn',
        'saavn': 'saavn',
        'soundcloud': 'soundcloud',
        'napster': 'napster',
        'tidal': 'tidal',
        'pandora': 'pandora',
        'joox': 'joox',
        'kkbox': 'kkbox',
        'line music': 'line_music',
        'netease': 'netease',
        'fizy': 'fizy',
        'flo': 'flo',
        'audiomack': 'audiomack',
        'anghami': 'anghami',
        'guvera': 'guvera',
        'google play': 'google_play',
        'google play music': 'google_play_music',
        'triller': 'triller',
        'trebel': 'trebel',
        'likee': 'likee',
        'iheart - thumbplay': 'iheart_thumbplay',
        'qobuz': 'qobuz',
        'qobuz stream': 'qobuz',
        'tdc a/s': 'tdc_as',
        'turkcell': 'turkcell',
        'audible magic': 'audible_magic',
        'beatport': 'beatport',
        'hitter - beeline kazakhstan': 'hitter_beeline_kz',
        'meta facebook instagram': 'facebook',
      };

      if (youtubeSet.has(p)) return 'youtube';
      if (vkSet.has(p)) return 'vkontakte';
      if (spotifySet.has(p)) return 'spotify';
      if (appleMusicSet.has(p)) return 'apple_music';
      if (itunesSet.has(p)) return 'itunes';
      if (amazonSet.has(p)) return 'amazon_music';
      if (instagramSet.has(p)) return 'instagram';
      if (facebookSet.has(p)) return 'facebook';
      if (tiktokSet.has(p)) return 'tiktok';
      if (tencentSet.has(p)) return 'tencent_music';
      if (yandexSet.has(p)) return 'yandex_music';

      if (directMap[p]) return directMap[p];
      return p.replace(/\s+/g, '_');
    },
  },

  watch: {
    "settings.selectedYear"() {
      this.fetchData();
    },
    // если меняется маршрут (добавили/убрали release_uuid) — перезагрузим данные
    '$route.params.release_uuid'() {
      this.fetchData();
    },
  },

  mounted() {
    this.fetchData();
  },
};
</script>

<style lang="scss" scoped>
.section {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  margin-bottom: 20px;
  padding: 25px;
  min-height: 60vh;
  color: white;
}

.section__loader {
  margin: 60px auto;
}

.section__wrapper {
  padding-top: 90px;
  width: 100%;
}
.section__header {
  margin-bottom: 22px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.section__heading {
  font-size: 1.188rem;
  font-weight: 500;
  span {
    color: #828282;
  }
}

.header__right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.upload-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.14);
  background: rgba(255,255,255,0.06);
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  transition: transform .06s ease, background .2s ease, border-color .2s ease;
  will-change: transform;
}
.upload-btn:hover {
  background: rgba(255,255,255,0.12);
  border-color: rgba(255,255,255,0.24);
}
.upload-btn:active {
  transform: translateY(1px);
}
.upload-btn:disabled {
  opacity: .6;
  cursor: not-allowed;
}
.upload-icon {
  width: 18px;
  height: 18px;
}

.hidden {
  display: none !important;
}

.year {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #ccc;
}
.year__select {
  padding: 6px 10px;
  border: 1px solid #3c3c3c;
  border-radius: 8px;
  background: #1e1e1e;
  color: #fff;
  font-size: 14px;
}

.section__block {
  &:not(:last-child) {
    margin-bottom: 48px;
  }
}

.section__linechart {
  flex: 1;
  height: 260px;
  border-radius: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  .chart {
    width: 100%;
    height: 100%;
  }
}

.section__stores {
  padding-left: 0;
}

.list {
  display: grid;
  row-gap: 8px;
  &--stores {
    grid-template-columns: 1fr 1fr;
  }
  &__item {
    position: relative;
    font-size: 1rem;
    p {
      margin-left: 16px;
    }
    span {
      color: #c7c7c7;
    }
    &::before {
      content: "";
      position: absolute;
      background: var(--color);
      left: 0;
      top: calc(50% - 4.5px);
      width: 8px;
      height: 8px;
      margin-right: 8px;
      border-radius: 100%;
    }
  }
}

.stores {
  display: flex;
  flex-flow: row wrap;
  justify-content: center;
  align-items: center;
  gap: 60px;

  &__piechart {
    width: 160px;
    height: 160px;
    position: relative;
  }
  &__total {
    width: inherit;
    height: inherit;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    font-size: 14px;
    font-weight: 600;
    text-align: center;
    line-height: 1;
    span {
      font-size: 12px;
      color: #c7c7c7;
    }
  }
}

/* ===== Modal ===== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.55);
  display: grid;
  place-items: center;
  z-index: 9999;
}
.modal-card {
  min-width: 260px;
  max-width: 90vw;
  padding: 20px 22px;
  border-radius: 14px;
  background: #121212;
  border: 1px solid rgba(255,255,255,0.14);
  box-shadow: 0 10px 40px rgba(0,0,0,.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  color: #fff;
}
.modal-spinner {
  width: 42px;
  height: 42px;
}
.modal-text {
  font-size: 14px;
  color: #e7e7e7;
}
</style>
